export interface FaqItem {
  question: string;
  answer: string;
}

export const TABLE_PLAN_FAQ_ITEMS: FaqItem[] = [
  {
    question: 'What is the Table Plan add-on?',
    answer: 'The Table Plan is a one-time purchase that gives you a full interactive seating planner inside your Wedsy dashboard. Place tables on a canvas, drag guests to seats, and export a print-ready PDF to hand to your venue.',
  },
  {
    question: 'How much does it cost?',
    answer: "It's £9.99 as a one-time payment — no subscription, no renewal. Buy it once and use it for every event you ever plan.",
  },
  {
    question: 'Is there a limit on tables or guests?',
    answer: 'No limits at all. Add as many tables and guests as your venue requires.',
  },
  {
    question: 'What table shapes are supported?',
    answer: "Round, rectangular, square, and top table. Mix and match shapes to match your exact venue layout — including a dedicated top table for the wedding party.",
  },
  {
    question: 'Can I save multiple seating plans?',
    answer: "Yes. Save multiple plans and switch between them — useful when you're weighing up different room layouts before you commit.",
  },
  {
    question: 'What export options are there?',
    answer: 'You can export a print-ready PDF — which includes a full room overview, individual table diagrams with seat assignments, and a complete guest list — or a CSV of your seating data. Both are one click from your dashboard.',
  },
  {
    question: 'Do I have to re-enter my guest list?',
    answer: 'No. Guests collected through your Wedsy invite appear automatically in the Table Plan — no importing or re-entering needed.',
  },
  {
    question: 'Can I use it for events other than weddings?',
    answer: 'Absolutely. The Table Plan works for any seated event — birthday dinners, gala evenings, corporate functions, and more. One purchase, unlimited events.',
  },
  {
    question: 'Is the Table Plan included in the Planner subscription?',
    answer: "Yes. If you're on the Planner subscription (for wedding planners managing multiple events), the Table Plan is included at no extra cost.",
  },
  {
    question: 'Is it a separate purchase from my invite?',
    answer: "Yes — the Table Plan is an optional add-on. Your Wedsy invite covers everything invite-related; the Table Plan is an upgrade for when you're ready to start planning seats.",
  },
];

export const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'Do my guests need to download an app?',
    answer: 'No app required. Guests open your invite link in any browser on any device — iPhone, Android, or desktop. They can RSVP instantly without downloading anything or creating an account.',
  },
  {
    question: 'Can I invite day guests and evening guests separately?',
    answer: "Yes. Tag guests as evening-only and they'll see the right schedule and details automatically. Day guests see the full day. One invite handles both.",
  },
  {
    question: 'How do guests RSVP?',
    answer: "Guests tap your invite link, view the invitation, and respond directly. No app to download, no account needed. You see their response instantly on your dashboard.",
  },
  {
    question: 'Is there a limit on guests?',
    answer: 'No. Invite as many guests as you like — unlimited is included.',
  },
  {
    question: 'What do I get when I publish?',
    answer: "Each published invite includes unlimited guests, 5 stunning templates, full customisation, RSVP tracking and reminders, dietary requirements, plus-one controls, guest tagging, private and public invite options, and QR code sharing — all for a one-time payment of £39. If you're a wedding planner, the Planner subscription at £29/month gives you up to 10 active published invites.",
  },
  {
    question: 'Do I need to pay again to send the full invite after my Save the Date?',
    answer: "No — it's all one invite. You pay once when you publish. Switch from Save the Date to full invite any time and all your sections unlock instantly, no extra charge.",
  },
  {
    question: 'Can I update my invite after sending it?',
    answer: 'Yes. Your invite is live — update any detail at any time and guests will always see the latest version. No need to resend.',
  },
  {
    question: 'Can I personalise my wedding invite for certain guests, like my wedding party or those invited to the rehearsal dinner?',
    answer: "Yes. Tag your guests and control exactly what each group sees — show or hide specific locations, schedule items, and RSVP questions depending on who's invited to what.",
  },
  {
    question: 'Can I ask guests custom questions in the RSVP?',
    answer: 'Yes. Add your own questions to the RSVP form — choose from multiple choice or free-text formats. Perfect for meal choices or anything else specific to your day. Answers appear on your guest dashboard.',
  },
  {
    question: 'Can my partner or wedding planner help manage the invite?',
    answer: 'Absolutely. Invite anyone to collaborate — they can edit invites and manage guests alongside you.',
  },
  {
    question: "Can I see a preview before I pay?",
    answer: "Yes. Browse our templates, see the animations, and explore the features. You only pay when you're ready to publish.",
  },
  {
    question: 'Do you offer custom designs?',
    answer: 'Yes. We offer a bespoke design service — a fully custom invitation designed to match your wedding theme, colours, and style. Get in touch at hello@wedsy.co to discuss your vision.',
  },
];

export const ANNIVERSARY_FAQ_ITEMS: FaqItem[] = [
  {
    question: 'How much does an anniversary invite cost?',
    answer: 'Just £19 — a one-time payment that covers everything. No subscription, no hidden fees.',
  },
  {
    question: "What's included?",
    answer: 'Everything you need for a celebration invite. Online RSVP, unlimited guests, your choice of design and colour palette, guest management, email sending, and a public link or QR code to share. No Wedsy branding on your invite.',
  },
  {
    question: 'Can I collect RSVPs?',
    answer: "Yes. Guests can RSVP directly through the invite. You'll see all responses on your dashboard in real time.",
  },
  {
    question: 'Can I personalise the design?',
    answer: "Yes. Choose from multiple themes, colour palettes, and font combinations. Add your names, date, venue, and schedule. It's your invite, made your way.",
  },
  {
    question: 'How do I share the invite?',
    answer: 'Send it via email directly from Wedsy, copy the link to share on WhatsApp or social, or download a QR code. You can also set it to private so only guests with the link can view it.',
  },
  {
    question: 'Do I need to pay again if I edit the invite after publishing?',
    answer: "No. Once you've paid, the invite is yours. Edit it as many times as you like. Guests will always see the latest version.",
  },
  {
    question: 'Can I see it before I pay?',
    answer: "Yes. Build your full invite and preview every design before committing. You only pay when you're ready to go live.",
  },
  {
    question: 'Does Wedsy work for other milestone celebrations?',
    answer: 'Yes. Wedsy works for any celebration, including significant birthdays, anniversaries, and more.',
  },
];

export const BIRTHDAY_FAQ_ITEMS: FaqItem[] = [
  {
    question: 'How much does a birthday invite cost?',
    answer: 'Just £19 — a one-time payment that covers everything. No subscription, no hidden fees.',
  },
  {
    question: "What's included?",
    answer: 'Everything you need for a birthday invite. Online RSVP, unlimited guests, your choice of design and colour palette, guest management, email sending, and a public link or QR code to share. No Wedsy branding on your invite.',
  },
  {
    question: 'Can I collect RSVPs and dietary requirements?',
    answer: "Yes. Guests RSVP directly through the invite and can provide dietary requirements at the same time. You'll see every response in real time on your dashboard.",
  },
  {
    question: 'Can I personalise it for a milestone birthday?',
    answer: "Absolutely. Add names, dates, venue details, and a custom schedule. Choose from multiple themes and colour palettes to match the mood of the occasion, whether it's a 30th, 40th, 50th, or any other milestone.",
  },
  {
    question: 'How do I share the invite?',
    answer: 'Send via email directly from Wedsy, copy the link to share on WhatsApp or social, or download a QR code for printed materials.',
  },
  {
    question: 'Can I update the invite after sending?',
    answer: "Yes. Your invite is live. Update any detail at any time and guests will always see the latest version. No need to resend.",
  },
  {
    question: 'Can I see it before I pay?',
    answer: "Yes. Build your full invite and preview every design before committing. You only pay when you're ready to go live.",
  },
  {
    question: 'Does Wedsy work for other celebrations?',
    answer: 'Yes. Wedsy works for any celebration. The same platform also handles anniversary invitations, and full wedding invitations are available from £39.',
  },
];
